
import { zodResolver } from '@hookform/resolvers/zod';
import { AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { useToast } from '@/components/ui/use-toast';
import {
  ResetPasswordRequestFormValues,
  resetPasswordRequestFormSchema,
} from '@/services/auth/form-schemas';
import { useResetPasswordRequest } from '@/services/auth/mutations';

import {
  AuthTemplate,
  FooterLinks,
} from '@/pages/unauthenticated/auth/components';

import { ResetPasswordForm } from './reset-password-form';
import { ResetPasswordRequestSuccess } from './reset-password-request-success';

export default function ResetPasswordRequestPage() {
  const { toast } = useToast();
  const [requested, setRequested] = useState(false);

  const form = useForm<ResetPasswordRequestFormValues>({
    resolver: zodResolver(resetPasswordRequestFormSchema),
    defaultValues: {
      email: '',
      isRetry: false,
    },
  });

  const { mutate, isPending } = useResetPasswordRequest({
    onSuccess: (_data, variables) => {
      if (variables.isRetry) {
        toast({
          variant: 'success',
          title: 'Check your email',
          description: `If we have ${variables.email} on file, we'll send a reset link.`,
        });
      } else {
        setRequested(true);
      }
    },
    onError: (error) => {
      const { title, message } = error.getErrorMessage({
        title: 'Uh oh! Something went wrong.',
        message:
          'There was a problem sending your password reset link. Please try again.',
      });
      toast({
        variant: 'destructive',
        title,
        description: Array.isArray(message) ? message.join(' ') : message,
      });
    },
  });

  const onSubmit = form.handleSubmit((data) => mutate(data));

  const showRequestForm = !requested;
  const email = form.getValues('email');

  const heading = showRequestForm ? 'Reset your password' : 'Check your email';

  return (
    <AnimatePresence mode="wait">
      <AuthTemplate
        footer={<FooterLinks />}
        heading={heading}
        subheading={
          showRequestForm ? (
            <ResetRequestSubheading />
          ) : (
            <ResetSuccessSubheading email={email} />
          )
        }
      >
        {showRequestForm ? (
          <ResetPasswordForm
            form={form}
            onSubmit={onSubmit}
            isPending={isPending}
          />
        ) : (
          <ResetPasswordRequestSuccess
            onResend={() => mutate({ email, isRetry: true })}
            isPending={isPending}
          />
        )}
      </AuthTemplate>
    </AnimatePresence>
  );
}

function ResetRequestSubheading() {
  return (
    <span>
      Enter the email address associated with your account and we&apos;ll send
      you a link to reset your password.
    </span>
  );
}

function ResetSuccessSubheading({ email }: { email: string }) {
  return (
    <div className="flex flex-col space-y-4">
      <div>
        Thanks! If <span className="font-semibold">{email}</span> matches an
        email we have on file, we&apos;ll send an email containing instructions
        for resetting your password.
      </div>
      <div>
        If you haven&apos;t received an email in 5 minutes, check your spam or
        junk folder, or click below to resend.
      </div>
    </div>
  );
}
